// Web client wrappers around the platform /api/support-cases endpoints.
//
// Every one of these is tied to the signed-in customer, so they all go through
// `userRequest` (which attaches the bearer token out of `persist:user` and
// stamps the market on the call). Nothing here is guest viewable, so there is
// no `publicRequest` variant, unlike reviewsApi.js.
//
// Only the CUSTOMER half of the route table is wrapped. Status changes,
// assignment and resolution belong to a vendor or to staff, so they have no
// business being callable from the storefront at all.
//
// Each helper resolves the parsed `res.data` envelope, the same way
// ticketingApi.js does, so a screen never touches an axios response.
import { userRequest } from "./requestMethods";

const BASE = "/api/support-cases";

/**
 * GET /api/support-cases/meta/options
 *
 * The live category, status and priority vocabularies. Clients must read them
 * from here rather than hardcoding an enum. utils/supportCaseVocabulary.js
 * normalises the answer and holds the shipped fallback for when this call
 * fails, so a customer on a bad connection can still report an issue.
 */
export const apiGetSupportCaseOptions = () =>
  userRequest.get(`${BASE}/meta/options`).then((r) => r.data);

/**
 * POST /api/support-cases
 *
 * payload: { subject, description, category, orderId?, vendorId?,
 *            evidence?: [{ url, type, caption }] }
 *
 * The server derives priority, routing and the response targets itself, so
 * nothing here may send them.
 */
export const apiCreateSupportCase = (payload) =>
  userRequest.post(BASE, payload).then((r) => r.data);

/**
 * GET /api/support-cases
 *
 * Scoped by the server to the caller's own cases, so this never asks for a
 * customer id. params: { status, category, q, page, limit, sort }.
 */
export const apiGetMySupportCases = (params = {}) =>
  userRequest.get(BASE, { params }).then((r) => r.data);

/** GET /api/support-cases/:caseId */
export const apiGetSupportCase = (caseId) =>
  userRequest.get(`${BASE}/${caseId}`).then((r) => r.data);

/**
 * POST /api/support-cases/:caseId/messages
 *
 * `isInternal` is deliberately never sent: an internal note is a vendor or
 * staff action, and a customer message is always visible to both sides.
 */
export const apiAddSupportCaseMessage = (caseId, { body, attachments } = {}) =>
  userRequest
    .post(`${BASE}/${caseId}/messages`, { body, attachments })
    .then((r) => r.data);

/** POST /api/support-cases/:caseId/evidence */
export const apiAddSupportCaseEvidence = (caseId, evidence) =>
  userRequest.post(`${BASE}/${caseId}/evidence`, { evidence }).then((r) => r.data);

/**
 * POST /api/support-cases/:caseId/escalate
 *
 * Open to the person who raised the case. The server decides the level and
 * whether the escalation is allowed at all.
 */
export const apiEscalateSupportCase = (caseId, reason) =>
  userRequest.post(`${BASE}/${caseId}/escalate`, { reason }).then((r) => r.data);

/**
 * POST /api/support-cases/:caseId/reopen
 *
 * The raiser may reopen within 14 days of resolution. The window is the
 * server's to enforce, so a refusal here is expected and has to read well.
 */
export const apiReopenSupportCase = (caseId, reason) =>
  userRequest.post(`${BASE}/${caseId}/reopen`, { reason }).then((r) => r.data);

/**
 * POST /api/support-cases/:caseId/satisfaction
 *
 * Raiser only, and only once the case is RESOLVED or CLOSED.
 * payload: { rating: 1..5, comment? }
 */
export const apiRateSupportCase = (caseId, { rating, comment } = {}) =>
  userRequest
    .post(`${BASE}/${caseId}/satisfaction`, { rating, comment })
    .then((r) => r.data);

/**
 * GET /api/support-cases/stats/summary
 *
 * Scoped like the list, so for a customer it counts their own cases.
 */
export const apiGetSupportCaseStats = (params = {}) =>
  userRequest.get(`${BASE}/stats/summary`, { params }).then((r) => r.data);

/**
 * The message to show when one of the calls above fails.
 *
 * The neighbouring controllers answer an error as `{ message, code }`, so this
 * reads that first, then axios's own message, and only then a fallback. Having
 * it here means every support case screen fails the same way.
 */
export const supportCaseErrorMessage = (error, fallback = "Something went wrong. Please try again.") =>
  error?.response?.data?.message ||
  error?.response?.data?.error?.message ||
  error?.message ||
  fallback;
